import {
  type Firestore,
  arrayRemove,
  arrayUnion,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

/**
 * LES PREFERENCES PERSONNELLES DE CONVERSATION (vague du soir, R12 et R4) :
 * un document par personne, `orgs/{orgId}/preferencesConversations/{uid}`,
 * lu et ecrit par son uid seul (rules) :
 * `{ orgId, uid, epinglees: RefConversation[], masquees: string[], updatedAt }`.
 *
 * - `epinglees` : l'ordre d'epinglage, stable (un ajout va en fin) ;
 * - `masquees` : les DIRECTS que la personne avait archives POUR ELLE. Depuis
 *   P18 (06/10/2026) un direct ne se supprime plus et le SDK ignore ce champ
 *   pour les directs ; il reste lu et ecrit, sans effet sur leur liste.
 */

export const PREFERENCES_CONVERSATIONS_COLLECTION = 'preferencesConversations';
/** Plafonds recopies dans les rules. */
export const EPINGLEES_MAX = 50;
export const MASQUEES_MAX = 200;

/** Une conversation, quelle que soit sa source : un sujet Lia ou une conversation du socle. */
export type RefConversation = `lia:${string}` | `conversation:${string}`;

export function refDeSujetLia(sujetId: string): RefConversation {
  return `lia:${sujetId}`;
}
export function refDeConversation(id: string): RefConversation {
  return `conversation:${id}`;
}
export function estRefConversation(valeur: unknown): valeur is RefConversation {
  return typeof valeur === 'string' && /^(lia|conversation):[A-Za-z0-9_-]{1,128}$/.test(valeur);
}

export interface PreferencesDeConversations {
  epinglees: RefConversation[];
  masquees: string[];
}

/** Pure : un document brut en preferences, sans doublon, les refs mal formees ignorees. */
export function preferencesDe(brut: unknown): PreferencesDeConversations {
  const d = (brut ?? {}) as Record<string, unknown>;
  const epinglees = Array.isArray(d['epinglees']) ? [...new Set(d['epinglees'].filter(estRefConversation))] : [];
  const masquees = Array.isArray(d['masquees'])
    ? [...new Set(d['masquees'].filter((v): v is string => typeof v === 'string' && v !== ''))]
    : [];
  return { epinglees, masquees };
}

export interface PreferencesConversationsRepository {
  observer(onChange: (p: PreferencesDeConversations) => void, onError?: (e: unknown) => void): () => void;
  observerEpinglees(onChange: (refs: RefConversation[]) => void, onError?: (e: unknown) => void): () => void;
  lire(): Promise<PreferencesDeConversations>;
  /** Epingle en FIN de liste (ordre stable). Sans effet si deja epinglee. */
  epingler(ref: RefConversation): Promise<void>;
  desepingler(ref: RefConversation): Promise<void>;
  /** Masque un direct pour la personne (son « archiver »). */
  masquer(conversationId: string): Promise<void>;
  afficher(conversationId: string): Promise<void>;
}

export function createPreferencesConversations(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): PreferencesConversationsRepository {
  const ref = () => doc(db, 'orgs', orgId, PREFERENCES_CONVERSATIONS_COLLECTION, getActorId());
  const ecrire = async (patch: Record<string, unknown>) => {
    await setDoc(ref(), { orgId, uid: getActorId(), ...patch, updatedAt: serverTimestamp() }, { merge: true });
  };
  const verifier = (r: unknown): RefConversation => {
    if (!estRefConversation(r)) throw new Error(`preferences : reference de conversation invalide (${String(r)}).`);
    return r;
  };
  return {
    observer(onChange, onError) {
      return onSnapshot(ref(), (snap) => onChange(preferencesDe(snap.exists() ? snap.data() : null)), onError);
    },
    observerEpinglees(onChange, onError) {
      return this.observer((p) => onChange(p.epinglees), onError);
    },
    async lire() {
      const snap = await getDoc(ref());
      return preferencesDe(snap.exists() ? snap.data() : null);
    },
    async epingler(r) {
      const actuelles = await this.lire();
      const valide = verifier(r);
      if (actuelles.epinglees.includes(valide)) return;
      if (actuelles.epinglees.length >= EPINGLEES_MAX) {
        throw new Error(`preferences : ${EPINGLEES_MAX} conversations epinglees au plus.`);
      }
      // `arrayUnion` ajoute en fin : l'ordre d'epinglage est l'ordre affiche.
      await ecrire({ epinglees: arrayUnion(valide) });
    },
    async desepingler(r) {
      await ecrire({ epinglees: arrayRemove(verifier(r)) });
    },
    async masquer(conversationId) {
      if (!conversationId) throw new Error('preferences.masquer : conversationId requis.');
      await ecrire({ masquees: arrayUnion(conversationId) });
    },
    async afficher(conversationId) {
      if (!conversationId) throw new Error('preferences.afficher : conversationId requis.');
      await ecrire({ masquees: arrayRemove(conversationId) });
    },
  };
}
